/**
 * Rung (a) — the signed-in person's own setting — and the part of it that is not obvious: WHEN IT
 * CHANGES WITHOUT A PAGE LOAD.
 *
 * LanguageContext used to read this at mount and treat that as the whole lifecycle, on the strength
 * of "every login and logout in this app is a full page navigation". Half of that is true. Login is
 * (routes/Login.jsx sets `window.location.href`). LOGOUT IS NOT: the button people actually press
 * clears storage and calls `navigate('/login')`, an SPA route change, and LanguageProvider sits
 * above `<App/>` in main.jsx and never unmounts. So after a sign-out the provider still held the
 * DEPARTED user's language, the sign-in page rendered in it, and the "English / Español" toggle —
 * the one control that exists to be reachable by somebody who cannot read the page it sits on — was
 * inert, because rung (a) outranks it and rung (a) was stale.
 *
 * Hence a signal. Anything that changes WHO IS SIGNED IN calls `accountLanguageChanged()` and the
 * provider re-reads the chain.
 *
 * WHY A SIGNAL AND NOT THE OBVIOUS ALTERNATIVES:
 *   * not a `storage` event — it does not fire in the tab that wrote the value, which is the only
 *     tab that matters here;
 *   * not a dependency on AuthContext — LanguageContext.jsx's header explains at length why the
 *     language must not be derived from async auth state (it would paint English and then swap,
 *     which is the exact failure this feature exists to remove);
 *   * not polling — a language that arrives a second late is a language that arrived wrong.
 *
 * The store is the SAME localStorage row AuthProvider keeps its user in, so there is one copy of the
 * answer rather than two that can disagree.
 */
import { USER_STORAGE_KEY } from '../../config/config.js'

const listeners = new Set()

/** Subscribe to "who is signed in, or what their language is, just changed". Returns unsubscribe. */
export function subscribeAccountLanguage(fn) {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** Tell every subscriber to re-read. Safe to call when nothing changed. */
export function accountLanguageChanged() {
  listeners.forEach((fn) => {
    try {
      fn()
    } catch (err) {
      // One bad subscriber must not stop the others, and must certainly not take a sign-out with it.
    }
  })
}

/** The signed-in account's saved preference, or null for "never chosen" (which is not English). */
export function readAccountLanguage() {
  try {
    const raw = window.localStorage.getItem(USER_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed.language === 'string' && parsed.language ? parsed.language : null
  } catch (err) {
    // A corrupt stored user is already survivable everywhere else in this app; it is certainly not
    // worth a blank screen over a language.
    return null
  }
}

/**
 * Write the language back onto the cached user, so the next page load resolves the same.
 *
 * SILENT ON PURPOSE — it does not fire the signal. Its callers are already holding the new value and
 * updating their own state with it; a notification here would only make them re-read storage for an
 * answer they just wrote. Call `accountLanguageChanged()` yourself when the change came from
 * somewhere the provider cannot see (a sign-out, or the server disagreeing on boot).
 */
export function cacheAccountLanguage(tag) {
  try {
    const raw = window.localStorage.getItem(USER_STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return
    parsed.language = tag || null
    window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(parsed))
  } catch (err) { /* see readAccountLanguage */ }
}
